import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Loader2, ChevronDown, ChevronUp, Droplets, Copy, CheckCircle2, Eye, ListOrdered } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";
import { SearchableSelect } from "./SearchableSelect";
import AddToItineraryButton from "./AddToItinerary";
import { Field, ProductInfoPanel, pickRecommendedDose, splitDose } from "./shared";
import { useCultureLookup } from "@/lib/cultures";

// Rappel « résultat observé » : apport d'au moins 15 jours sans résultat renseigné
const RESULTS_DELAY_DAYS = 15;
const resultsDue = (item) => {
  if (item.results_observed || !item.date) return false;
  const d = new Date(`${item.date}T00:00:00`);
  return !isNaN(d) && (Date.now() - d.getTime()) / 86400000 >= RESULTS_DELAY_DAYS;
};

/** Saisie du résultat observé après coup (bouton dans la fiche de l'apport). */
function ResultsEditor({ item, onSaved, autoOpen }) {
  const [open, setOpen] = useState(!!autoOpen);
  const [text, setText] = useState(item.results_observed || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/biostimulants/${item.biostimulant_id}/results`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ results_observed: text }),
      });
      if (!res.ok) throw new Error();
      toast.success("Résultat enregistré"); setOpen(false); onSaved();
      window.dispatchEvent(new Event("veyer:reminders-changed")); // met à jour la cloche
    } catch { toast.error("Enregistrement impossible"); }
    setSaving(false);
  };
  if (!open) {
    return (
      <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg" style={{ color: "#0891B2" }}
        onClick={() => setOpen(true)} data-testid={`bio-results-btn-${item.biostimulant_id}`}>
        <Eye className="w-3 h-3" /> {item.results_observed ? "Modifier le résultat" : "Noter le résultat"}
      </Button>
    );
  }
  return (
    <div className="w-full space-y-1.5 rounded-lg p-2" style={{ background: "#0891B20D" }} data-testid={`bio-results-editor-${item.biostimulant_id}`}>
      <Label className="text-[10px] uppercase" style={{ color: "#0E7490" }}>Résultat observé</Label>
      <Textarea value={text} onChange={e => setText(e.target.value)} rows={2} autoFocus
        placeholder="Ex : meilleure vigueur, feuillage plus vert, aucun effet visible…" className="text-xs bg-white" />
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setOpen(false)}>Annuler</Button>
        <Button size="sm" className="h-7 text-[10px] text-white" style={{ background: "#0891B2" }} onClick={save} disabled={saving}>
          {saving && <Loader2 className="w-3 h-3 animate-spin mr-1" />} Enregistrer
        </Button>
      </div>
    </div>
  );
}

const PRODUCT_TYPE_LABELS = {
  biostimulant: { label: "Biostimulant", color: "#0891B2", bg: "#0891B215" },
};

export default function BiostimulantTab({ parcelId, refData, surfaceHa, cultureType, ilotId, ilots = [], onOpenItineraries }) {
  const ilotName = (id) => (ilots.find((i) => i.ilot_id === id) || {}).name;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [duplicateFrom, setDuplicateFrom] = useState(null);
  // Lien du rappel (cloche) : ?bio=<id> ouvre directement cet apport et sa case « Résultat observé »
  const location = useLocation();
  const focusBio = new URLSearchParams(location.search).get("bio");
  useEffect(() => { if (focusBio) setExpandedId(focusBio); }, [focusBio]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/biostimulants?parcel_id=${parcelId}${ilotId ? `&ilot_id=${ilotId}` : ""}`);
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error('[BiostimulantTab] fetch failed:', err); }
    setLoading(false);
  }, [parcelId, ilotId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (id) => {
    try {
      await authFetch(`${API_BASE}/carnet/biostimulants/${id}`, { method: "DELETE" });
      toast.success("Supprime"); setDeletingId(null); fetchData();
    } catch { toast.error("Erreur"); setDeletingId(null); }
  };

  if (showForm) {
    return <BiostimulantForm parcelId={parcelId} ilotId={ilotId} refData={refData} surfaceHa={surfaceHa} cultureType={cultureType}
      initial={duplicateFrom}
      onSaved={() => { setShowForm(false); setDuplicateFrom(null); fetchData(); }}
      onCancel={() => { setShowForm(false); setDuplicateFrom(null); }} />;
  }

  const confirmCopy = async (id) => {
    try {
      const res = await authFetch(`${API_BASE}/carnet/duplicate/confirm/biostimulant/${id}`, { method: "POST" });
      if (res.ok) { toast.success("Copie confirmée"); fetchData(); } else toast.error("Confirmation impossible");
    } catch { toast.error("Erreur de connexion"); }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 border-b border-black/5">
        <Button size="sm" className="w-full h-9 text-[11px] font-semibold rounded-lg text-white gap-1.5"
          style={{ background: "#0891B2" }}
          onClick={() => { setDuplicateFrom(null); setShowForm(true); }} data-testid="bio-new-btn">
          <Plus className="w-3.5 h-3.5" /> Nouvel apport biostimulant
        </Button>
        {onOpenItineraries && (
          <Button size="sm" variant="outline" className="w-full h-8 mt-2 text-[11px] font-semibold rounded-lg gap-1.5"
            onClick={onOpenItineraries} data-testid="bio-itineraries-btn">
            <ListOrdered className="w-3.5 h-3.5" /> Itinéraire technique
          </Button>
        )}
      </div>
      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "#0891B2" }} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-4">
            <Droplets className="w-8 h-8 mx-auto mb-3 opacity-20" />
            <p className="text-xs" style={{ color: "#7A7A7A" }}>Aucun biostimulant ou produit de biocontrole</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {items.map(item => {
              const expanded = expandedId === item.biostimulant_id;
              const typeInfo = PRODUCT_TYPE_LABELS[item.product_type] || PRODUCT_TYPE_LABELS.biostimulant;
              return (
                <div key={item.biostimulant_id} className="rounded-xl border border-black/5 overflow-hidden" data-testid={`bio-${item.biostimulant_id}`}>
                  <button onClick={() => setExpandedId(expanded ? null : item.biostimulant_id)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-black/[0.02]">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: typeInfo.bg }}>
                      <Droplets className="w-4 h-4" style={{ color: typeInfo.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: "#1A1A1A" }}>{item.product_name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: typeInfo.bg, color: typeInfo.color }}>{typeInfo.label}</Badge>
                        {item.dose && <span className="text-[10px]" style={{ color: "#4A4A4A" }}>{item.dose} {item.unit}</span>}
                        <span className="text-[10px]" style={{ color: "#AAAAAA" }}>{item.date}</span>
                        {item.ilot_id && ilotName(item.ilot_id) && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#dbe8f1", color: "#2f6a92" }}>{ilotName(item.ilot_id)}</Badge>}
                        {resultsDue(item) && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#FDECC8", color: "#92400E" }} data-testid={`bio-results-due-${item.biostimulant_id}`}>Résultat à noter</Badge>}
                        {item.status === "a_verifier" && <Badge className="text-[9px] border-0 px-1.5 py-0" style={{ background: "#FBF1DC", color: "#9A6A12" }}>{item.itinerary ? `Itinéraire ${item.itinerary.step}/${item.itinerary.steps} · à vérifier` : "Copie à vérifier"}</Badge>}
                      </div>
                    </div>
                    {expanded ? <ChevronUp className="w-3.5 h-3.5 opacity-40" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40" />}
                  </button>
                  {expanded && (
                    <div className="px-3 pb-3 border-t border-black/5 pt-2 space-y-1.5">
                      {[["Formule", item.composition?.formule], ["Methode", item.method], ["Surface", item.surface_applied ? `${item.surface_applied} ha` : null],
                        ["Cible", item.target], ["Frequence", item.frequency],
                        ["Resultats", item.results_observed], ["Operateur", item.operateur],
                        ["Notes", item.notes],
                      ].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex text-[11px]">
                          <span className="w-24 shrink-0 font-medium" style={{ color: "#7A7A7A" }}>{k}</span>
                          <span style={{ color: "#1A1A1A" }}>{v}</span>
                        </div>
                      ))}
                      <div className="flex gap-2 mt-2 pt-2 border-t border-black/5 flex-wrap">
                        <ResultsEditor key={`${item.biostimulant_id}-${item.results_observed || ""}`} item={item} onSaved={fetchData}
                          autoOpen={focusBio === item.biostimulant_id && !item.results_observed} />
                        {item.status === "a_verifier" && (
                          <Button size="sm" className="h-7 text-[10px] gap-1 rounded-lg text-white" style={{ background: "#2D6A4F" }}
                            onClick={() => confirmCopy(item.biostimulant_id)} data-testid={`bio-confirm-${item.biostimulant_id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Confirmer
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg"
                          onClick={() => { setDuplicateFrom(item); setShowForm(true); }} data-testid={`bio-duplicate-${item.biostimulant_id}`}>
                          <Copy className="w-3 h-3" /> Dupliquer
                        </Button>
                        <AddToItineraryButton kind="biostimulant" item={item} color="#0891B2" />
                        <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1 rounded-lg text-red-500"
                          onClick={() => setDeletingId(item.biostimulant_id)}>
                          <Trash2 className="w-3 h-3" /> Supprimer
                        </Button>
                        {deletingId === item.biostimulant_id && (
                          <div className="flex items-center gap-1.5 ml-1">
                            <span className="text-[10px] font-medium" style={{ color: "#DC2626" }}>Confirmer ?</span>
                            <Button size="sm" variant="outline" className="h-6 text-[9px] px-2 rounded-lg border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(item.biostimulant_id)} data-testid="confirm-delete-btn">Oui</Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[9px] px-1" onClick={() => setDeletingId(null)}>Non</Button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

function BiostimulantForm({ parcelId, ilotId, refData, surfaceHa, cultureType, initial, onSaved, onCancel }) {
  const known = (refData?.biostimulants || []).some(p => p.name === initial?.product_name);
  const [productName, setProductName] = useState(initial ? (known ? initial.product_name : "Autre") : "");
  const [customProduct, setCustomProduct] = useState(initial && !known ? (initial.product_name || "") : "");
  // Anciennes saisies « 3.0 L/ha » : dose et unité séparées
  const initialDose = splitDose(initial?.dose);
  const [dose, setDose] = useState(initialDose ? initialDose.value : (initial?.dose ?? ""));
  const [unit, setUnit] = useState((initialDose && initialDose.unit) || initial?.unit || "L/ha");
  const [method, setMethod] = useState(initial?.method || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [surfaceApplied, setSurfaceApplied] = useState(surfaceHa?.toString() || "");
  const [target, setTarget] = useState(initial?.target || cultureType || "");
  // Fréquence : plus saisie (les apports répétés se dupliquent), conservée pour les saisies existantes
  const frequency = initial?.frequency || "";
  const [resultsObserved, setResultsObserved] = useState("");
  const [operateur, setOperateur] = useState(initial?.operateur || "");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const cultureOf = useCultureLookup();
  const allProducts = refData?.biostimulants || [];
  const selectedProduct = allProducts.find(p => p.name === productName);

  const handleProductSelect = (v) => {
    setProductName(v);
    if (v !== "Autre") setCustomProduct("");
    const p = allProducts.find(x => x.name === v);
    if (p) {
      // Dose recommandée pour la culture (groupes E-Phy du référentiel), dose et unité séparées
      const rec = pickRecommendedDose(p, cultureType, cultureOf(cultureType));
      const d = splitDose(rec.raw);
      setDose(d ? d.value : "");
      const u = (d && d.unit) || p.unit;
      if (u) setUnit(u);
    }
  };

  const handleSubmit = async () => {
    if (!productName) { toast.error("Selectionnez un produit"); return; }
    if (productName === "Autre" && !customProduct.trim()) { toast.error("Precisez le produit"); return; }
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/carnet/biostimulants`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          parcel_id: parcelId, ilot_id: ilotId || null, date, product_name: productName,
          custom_product: productName === "Autre" ? customProduct : null,
          product_type: "biostimulant", dose, unit, method,
          surface_applied: surfaceApplied ? parseFloat(surfaceApplied) : null,
          target, frequency, results_observed: resultsObserved, operateur, notes,
        }),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      toast.success("Apport enregistre");
      onSaved();
    } catch (e) { toast.error(e.message || "Erreur"); }
    setSaving(false);
  };

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-3" data-testid="bio-form">
        <div className="flex items-center gap-2 pb-2 border-b border-black/5">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onCancel}><ArrowLeft className="w-4 h-4" /></Button>
          <h3 className="text-sm font-bold" style={{ color: "#1A1A1A" }}>{initial ? "Dupliquer l'apport" : "Nouvel apport biostimulant"}</h3>
        </div>

        <Field label="Date" value={date} onChange={setDate} type="date" required testId="bio-date" />

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Produit *</Label>
          <SearchableSelect
            items={[...allProducts.map(p => ({ value: p.name, label: p.name, sublabel: [p.formule, p.amm ? `${p.amm}${p.info ? ' - ' + p.info.slice(0, 40) : ''}` : (p.info || '').slice(0, 50)].filter(Boolean).join(" · ") })), { value: "Autre", label: "Autre (personnalise)" }]}
            value={productName}
            onSelect={handleProductSelect}
            placeholder="Rechercher un biostimulant..."
            searchPlaceholder="Nom, AMM ou composition..."
            testId="bio-product-select"
            className="mt-1 text-sm"
          />
        </div>

        {selectedProduct && <ProductInfoPanel product={selectedProduct} cultureType={cultureType} surfaceHa={surfaceHa} />}

        <Field label="Produit absent de la liste ? Saisissez son nom" value={customProduct}
          onChange={(v) => { setCustomProduct(v); if (v.trim()) setProductName("Autre"); else if (productName === "Autre") setProductName(""); }}
          placeholder="Nom du produit, formule (ex. 15-12-24)…" testId="bio-custom-product" />

        <div className="grid grid-cols-2 gap-3">
          <Field label="Dose" type="number" value={dose} onChange={setDose} placeholder="Ex: 2" testId="bio-dose" />
          <div>
            <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Unite</Label>
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="h-9 text-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="z-[9999]">
                {[...new Set([...["L/ha", "kg/ha", "g/ha", "mL/L", "g/L", "T/ha", "unite/ha"], unit].filter(Boolean))].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Methode d'application</Label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="h-9 text-sm mt-1"><SelectValue placeholder="Selectionnez..." /></SelectTrigger>
            <SelectContent className="z-[9999]">
              {(refData?.methodes_application_bio || []).map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Culture ciblee" value={target} onChange={setTarget} placeholder="Ex: Canne a sucre" testId="bio-target" />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Surface (ha)" value={surfaceApplied} onChange={setSurfaceApplied} type="number" testId="bio-surface" />
          <Field label="Operateur" value={operateur} onChange={setOperateur} testId="bio-operateur" />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Resultats observes</Label>
          <Textarea value={resultsObserved} onChange={e => setResultsObserved(e.target.value)}
            placeholder="Effets constates apres application..." className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="bio-results" />
        </div>

        <div>
          <Label className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Notes</Label>
          <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Observations..."
            className="text-sm bg-white border-black/10 min-h-[50px] mt-1" data-testid="bio-notes" />
        </div>

        <Button onClick={handleSubmit} disabled={saving}
          className="w-full h-10 text-sm font-semibold rounded-xl text-white gap-2" style={{ background: "#0891B2" }}
          data-testid="bio-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Enregistrer
        </Button>
      </div>
    </ScrollArea>
  );
}


// ==================== AMENDA TAB (CIRAD Calculator) ====================
import { Switch } from "@/components/ui/switch";
import { Info } from "lucide-react";
